import React, {useState, useMemo} from "react";
import {Tooltip, Pagination} from "antd";
import moment from "moment";
import {useStore, useFetch} from "@vigo/hooks";
import {useTranslation} from "react-i18next";
import {
    getModelHumanReadable,
    formatMomentDate,
    getBundleIdAsDate,
    isFieldChanged
} from "utils/helpers";
import {InfoCircleOutlined} from "@ant-design/icons";
import {deviceOSEnum} from "constants/constants";
import {VText} from "components/_defaultComponents/Text/VText";
import {VFlex} from "components/_defaultComponents/Flex/VFlex";
import {
    HighlightedCell,
    StyledModal,
    StyledTable
} from "./style/CourierSummarDeviceHistoryModalInformation.styled";

const PAGE_SIZE = 8;

export const CourierSummarDeviceHistoryModalInformation = ({
    courierId,
    title,
    visible,
    onClose
}) => {
    const {t} = useTranslation(["courierDetail"]);
    const stores = useStore();
    const [page, setPage] = useState(0);

    const [deviceInfoHistoryLoading, deviceInfoHistoryResult] = useFetch({
        defaultDataValue: [],
        fetchFn: stores.courierStore.getCourierDeviceInfoHistory.bind(null, {
            courierId,
            skip: (page - 1) * PAGE_SIZE,
            limit: PAGE_SIZE
        }),
        dependencies: [courierId, visible, page],
        initialCall: Boolean(courierId),
        condition: Boolean(courierId) && visible
    });

    const dataSource = deviceInfoHistoryResult?.result || [];
    const isFirstPage = page === 0 || page === 1;
    const previousRow = dataSource?.length > 1 ? dataSource?.[1] : null;
    const renderCellWithHighlight = (value, row, index, fieldPath, renderFn) => {
        if (!isFirstPage || index !== 0) {
            const content = renderFn ? renderFn(value, row) : value;
            return content;
        }
        const isChanged = isFieldChanged(row, previousRow, fieldPath);
        const content = renderFn ? renderFn(value, row) : value;

        if (isChanged) {
            return <HighlightedCell>{content}</HighlightedCell>;
        }

        return content;
    };

    const columns = useMemo(
        () => [
            {
                title: t("fields.deviceBrand"),
                dataIndex: "info.brand",
                key: "info.brand",
                align: "center",
                onCell: () => ({
                    "data-label": t("fields.deviceBrand")
                }),
                render: (_, row, index) => {
                    const value = row?.info?.brand || "-";
                    return renderCellWithHighlight(value, row, index, "info.brand", () => value);
                }
            },
            {
                title: t("fields.deviceModel"),
                dataIndex: "info.model",
                key: "info.model",
                align: "center",
                onCell: () => ({
                    "data-label": t("fields.deviceModel")
                }),
                render: (_, row, index) => {
                    const value = getModelHumanReadable(row?.info?.model, row?.info?.os) || "-";
                    return renderCellWithHighlight(value, row, index, "info.model", () => value);
                }
            },
            {
                title: t("fields.deviceOperatingSystem"),
                dataIndex: "info.os",
                key: "info.os",
                align: "center",
                onCell: () => ({
                    "data-label": t("fields.deviceOperatingSystem")
                }),
                render: (_, row, index) => {
                    const osName =
                        row?.info?.os === "ios" || row?.info?.os === "iOS"
                            ? deviceOSEnum.IOS
                            : deviceOSEnum.ANDROID;
                    return renderCellWithHighlight(osName, row, index, "info.os", () => osName);
                }
            },
            {
                title: t("fields.deviceOperatingSystemVersion"),
                dataIndex: "info.osVersion",
                key: "info.osVersion",
                align: "center",
                onCell: () => ({
                    "data-label": t("fields.deviceOperatingSystemVersion")
                }),
                render: (_, row, index) => {
                    const value = row?.info?.osVersion || "-";
                    return renderCellWithHighlight(
                        value,
                        row,
                        index,
                        "info.osVersion",
                        () => value
                    );
                }
            },
            {
                title: t("fields.versionName"),
                dataIndex: "info.version",
                key: "info.version",
                align: "center",
                onCell: () => ({
                    "data-label": t("fields.versionName")
                }),
                render: (_, row, index) => {
                    const value = row?.info?.version || "-";
                    return renderCellWithHighlight(value, row, index, "info.version", () => value);
                }
            },
            {
                title: t("fields.currentBundleId"),
                dataIndex: "info.bundleId",
                key: "info.bundleId",
                align: "center",
                onCell: () => ({
                    "data-label": t("fields.currentBundleId")
                }),
                render: (_, row, index) => {
                    const bundleIdDate = row?.info?.bundleId
                        ? moment(getBundleIdAsDate(row?.info?.bundleId))
                        : null;
                    const isValidDate = bundleIdDate && bundleIdDate.isValid();
                    const content = (
                        <Tooltip title={getBundleIdAsDate(row?.info?.bundleId, true)}>
                            {isValidDate ? bundleIdDate.fromNow() : "Geçersiz Tarih"}{" "}
                            <InfoCircleOutlined />
                        </Tooltip>
                    );
                    return renderCellWithHighlight(
                        null,
                        row,
                        index,
                        "info.bundleId",
                        () => content
                    );
                }
            },
            {
                title: "Başlangıç Paket Versiyonu",
                dataIndex: "info.minBundleId",
                key: "info.minBundleId",
                align: "center",
                onCell: () => ({
                    "data-label": "Başlangıç Paket Versiyonu"
                }),
                render: (_, row, index) => {
                    const minBundleIdDate = row?.info?.minBundleId
                        ? moment(getBundleIdAsDate(row?.info?.minBundleId))
                        : null;
                    const isValidDate = minBundleIdDate && minBundleIdDate.isValid();
                    const content = (
                        <Tooltip title={getBundleIdAsDate(row?.info?.minBundleId, true)}>
                            {isValidDate ? minBundleIdDate.fromNow() : "Geçersiz Tarih"}{" "}
                            <InfoCircleOutlined />
                        </Tooltip>
                    );
                    return renderCellWithHighlight(
                        null,
                        row,
                        index,
                        "info.minBundleId",
                        () => content
                    );
                }
            },
            {
                title: t("fields.deviceNotificationPermission.title"),
                dataIndex: "info.notificationPermission",
                key: "info.notificationPermission",
                align: "center",
                onCell: () => ({
                    "data-label": t("fields.deviceNotificationPermission.title")
                }),
                render: (_, row, index) => {
                    const value =
                        row?.info?.notificationPermission === true
                            ? t("fields.deviceNotificationPermission.yes")
                            : row?.info?.notificationPermission === false
                            ? t("fields.deviceNotificationPermission.no")
                            : t("fields.deviceNotificationPermission.unknown");
                    return renderCellWithHighlight(
                        value,
                        row,
                        index,
                        "info.notificationPermission",
                        () => value
                    );
                }
            },
            {
                title: t("fields.deviceAlwaysLocationPermission.title"),
                dataIndex: "info.alwaysLocationPermission",
                key: "info.alwaysLocationPermission",
                align: "center",
                onCell: () => ({
                    "data-label": t("fields.deviceAlwaysLocationPermission.title")
                }),
                render: (_, row, index) => {
                    const value =
                        row?.info?.alwaysLocationPermission === true
                            ? t("fields.deviceAlwaysLocationPermission.yes")
                            : row?.info?.alwaysLocationPermission === false
                            ? t("fields.deviceAlwaysLocationPermission.no")
                            : t("fields.deviceAlwaysLocationPermission.unknown");
                    return renderCellWithHighlight(
                        value,
                        row,
                        index,
                        "info.alwaysLocationPermission",
                        () => value
                    );
                }
            },
            {
                title: t("fields.deviceNotificationDetails.title"),
                key: "notificationDetails",
                align: "center",
                onCell: () => ({"data-label": t("fields.deviceNotificationDetails.title")}),
                render: (_, row, index) => {
                    const info = row?.info || {};
                    const boolLabel = (val, yes, no, na) =>
                        val === null || val === undefined ? (na || "—") : val ? yes : no;

                    const token = info.fcmToken;
                    const shortToken = token
                        ? `${token.substring(0, 8)}...${token.substring(token.length - 8)}`
                        : t("fields.deviceFcmToken.empty");

                    const tooltipContent = (
                        <div style={{fontSize: 12, lineHeight: "22px", minWidth: 260}}>
                            <div>
                                <b>{t("fields.deviceFirebaseInitialized.title")}:</b>{" "}
                                {boolLabel(
                                    info.firebaseInitialized,
                                    t("fields.deviceFirebaseInitialized.yes"),
                                    t("fields.deviceFirebaseInitialized.no"),
                                    t("fields.deviceFirebaseInitialized.unknown")
                                )}
                            </div>
                            <div>
                                <b>{t("fields.devicePermissionGranted.title")}:</b>{" "}
                                {boolLabel(
                                    info.permissionGranted,
                                    t("fields.devicePermissionGranted.yes"),
                                    t("fields.devicePermissionGranted.no"),
                                    t("fields.devicePermissionGranted.unknown")
                                )}
                            </div>
                            <div>
                                <b>{t("fields.deviceRegistered.title")}:</b>{" "}
                                {boolLabel(
                                    info.deviceRegistered,
                                    t("fields.deviceRegistered.yes"),
                                    t("fields.deviceRegistered.no"),
                                    t("fields.deviceRegistered.unknown")
                                )}
                            </div>
                            <div>
                                <b>{t("fields.deviceTokenReceived.title")}:</b>{" "}
                                {boolLabel(
                                    info.tokenReceived,
                                    t("fields.deviceTokenReceived.yes"),
                                    t("fields.deviceTokenReceived.no"),
                                    t("fields.deviceTokenReceived.unknown")
                                )}
                            </div>
                            <div>
                                <b>{t("fields.devicePlayServicesAvailable.title")}:</b>{" "}
                                {boolLabel(
                                    info.playServicesAvailable,
                                    t("fields.devicePlayServicesAvailable.yes"),
                                    t("fields.devicePlayServicesAvailable.no"),
                                    t("fields.devicePlayServicesAvailable.notApplicable")
                                )}
                            </div>
                            <div>
                                <b>{t("fields.deviceApnsTokenAvailable.title")}:</b>{" "}
                                {boolLabel(
                                    info.apnsTokenAvailable,
                                    t("fields.deviceApnsTokenAvailable.yes"),
                                    t("fields.deviceApnsTokenAvailable.no"),
                                    t("fields.deviceApnsTokenAvailable.notApplicable")
                                )}
                            </div>
                            <div>
                                <b>{t("fields.deviceFcmToken.title")}:</b>{" "}
                                {token ? (
                                    <span
                                        title={token}
                                        style={{cursor: "pointer", wordBreak: "break-all"}}
                                    >
                                        {shortToken}
                                    </span>
                                ) : (
                                    t("fields.deviceFcmToken.empty")
                                )}
                            </div>
                        </div>
                    );

                    const hasAnyValue =
                        info.firebaseInitialized != null ||
                        info.permissionGranted != null ||
                        info.deviceRegistered != null ||
                        info.tokenReceived != null ||
                        info.playServicesAvailable != null ||
                        info.apnsTokenAvailable != null ||
                        info.fcmToken;

                    const content = (
                        <Tooltip title={tooltipContent} placement="left">
                            <VText style={{cursor: "pointer", color: hasAnyValue ? "#1890ff" : "#999"}}>
                                <InfoCircleOutlined /> {hasAnyValue ? t("fields.deviceNotificationDetails.view") : "—"}
                            </VText>
                        </Tooltip>
                    );

                    const changedFields = [
                        "info.firebaseInitialized",
                        "info.permissionGranted",
                        "info.deviceRegistered",
                        "info.tokenReceived",
                        "info.playServicesAvailable",
                        "info.apnsTokenAvailable",
                        "info.fcmToken"
                    ];
                    const isAnyChanged =
                        isFirstPage &&
                        index === 0 &&
                        changedFields.some(f => isFieldChanged(row, previousRow, f));

                    return isAnyChanged ? <HighlightedCell>{content}</HighlightedCell> : content;
                }
            },
            {
                title: "Değiştirilme Tarihi",
                dataIndex: "createdAt",
                key: "createdAt",
                align: "center",
                onCell: () => ({
                    "data-label": "Değiştirilme Tarihi"
                }),
                render: (_, row, index) => {
                    const content = (
                        <Tooltip title={formatMomentDate(row?.createdAt)}>
                            {moment(row?.createdAt).fromNow()} <InfoCircleOutlined />
                        </Tooltip>
                    );
                    return renderCellWithHighlight(null, row, index, "createdAt", () => content);
                }
            }
        ],
        [t, dataSource, isFirstPage, previousRow]
    );

    return (
        <StyledModal
            title={
                <VText fontSize={16} fontWeight={600}>
                    {title}
                </VText>
            }
            visible={visible}
            onCancel={onClose}
            okText="Kapat"
            onOk={onClose}
            width={"auto"}
            style={{maxWidth: "1000px"}}
            wrapClassName="courier-device-history-modal"
            cancelButtonProps={{style: {display: "none"}}}
        >
            <StyledTable
                size="small"
                dataSource={dataSource}
                locale={{
                    emptyText: "Geçmişe yönelik cihaz bilgisi bulunamadı."
                }}
                rowKey="_id"
                columns={columns}
                loading={deviceInfoHistoryLoading}
                pagination={false}
                scroll={{x: 900}}
            />
            {previousRow && (
                <VFlex align="center" gap={4} style={{marginTop: "16px", marginBottom: "16px"}}>
                    <VFlex
                        align="center"
                        style={{
                            background: "#fff3cd",
                            border: "1px solid #f6e4a7",
                            padding: "8px",
                            borderRadius: "4px"
                        }}
                    />
                    <VText fontSize={10} fontWeight={400}>
                        Aktif cihaz bilgisindeki son değişikliği gösterir.
                    </VText>
                </VFlex>
            )}
            <VFlex justify="center">
                <Pagination
                    total={deviceInfoHistoryResult?.count || 0}
                    current={page === 0 ? 1 : page}
                    hideOnSinglePage={true}
                    showSizeChanger={false}
                    pageSize={PAGE_SIZE}
                    onChange={page => {
                        setPage(page);
                    }}
                />
            </VFlex>
        </StyledModal>
    );
};
